<script lang="ts">
    import { onMount } from 'svelte'

    import { isEqual } from 'lodash-es'

    import { gettext as _ } from 'hydria/services/gettext'
    import { notify } from 'hydria/services/notifications'
    import { simpleFetch } from 'hydria/services/session'

    import LoadingPattern from 'hydria/components/LoadingPattern.svelte'

    interface Props {
        userId: string
    }

    let { userId }: Props = $props()

    interface Form {
        quotation: boolean
        'bill later': boolean
        'credit card': boolean
        stripe: boolean
    }
    let saved: Form | undefined = $state()
    let form: Form | undefined = $state()

    let changed = $derived(!isEqual(saved, form))

    let loading = $state(true)
    let error = $state(false)
    let saving = $state(false)
    let loadedUserId: string | undefined | null = $state()

    async function load(userId: string) {
        const userIdToLoad = userId ? userId : ''
        if (loadedUserId == userIdToLoad) return
        loadedUserId = userIdToLoad
        loading = true

        try {
            const r = await simpleFetch('GET', `/json/ecom/1/user-payment-types/${userId}`)
            const types = r.data.types != null ? r.data.types : []
            saved = {
                quotation: types.includes('quotation'),
                'bill later': types.includes('bill later'),
                'credit card': types.includes('credit card'),
                stripe: types.includes('stripe'),
            }
            form = { ...saved }
        } catch (e) {
            console.error('Failed to load payment types:', e)
            notify({
                type: 'danger',
                title: $_('Modes de paiement'),
                text: 'L’information n’a pas pu être chargée.',
            })
            error = true
        }

        loading = false
    }

    async function save() {
        if (!form) {
            return
        }
        saving = true

        const data = { types: [] as string[] }
        if (form['bill later']) data.types.push('bill later')
        if (form['quotation']) data.types.push('quotation')
        if (form['credit card']) data.types.push('credit card')
        if (form['stripe']) data.types.push('stripe')
        try {
            const r = await simpleFetch('PUT', `/json/ecom/1/user-payment-types/${userId}`, data)
            const types = r.data.types != null ? r.data.types : []
            saved = {
                quotation: types.includes('quotation'),
                'bill later': types.includes('bill later'),
                'credit card': types.includes('credit card'),
                stripe: types.includes('stripe'),
            }
            form = { ...saved }
            notify({
                type: 'success',
                title: $_('Modes de paiement'),
                text: 'Information sauvegardée.',
            })
        } catch (e) {
            console.error('Failed to save payment types:', e)
            notify({
                type: 'danger',
                title: $_('Modes de paiement'),
                text: 'Les Modes de paiement n’ont pas pu être sauvegardés.',
            })
            error = true
        }

        saving = false
    }

    $effect(() => {
        load(userId)
    })
</script>

<div class="card" style="height: 100%;">
    <div class="card-title text-primary">{$_('Modes de paiement')}</div>
    {#if loading || !form}
        <div class="card-body"><LoadingPattern pattern={['x', 'x', 'x']} /></div>
    {:else if error}
        <div class="card-body text-danger">L’information n’a pas pu être chargée.</div>
    {:else}
        <div class="card-body">
            <form
                onsubmit={(e) => {
                    e.preventDefault()
                    save()
                }}
                class="d-grid gap-2"
            >
                <div class="row">
                    <label for="tlde-mp-blater" class="col-sm-3 col-form-label pt-0">{$_('Bill later')}</label>
                    <div class="col-sm-9">
                        <div class="form-check">
                            <input class="form-check-input" type="checkbox" id="tlde-mp-blater" bind:checked={form['bill later']} />
                            <label for="tlde-mp-blater" class="form-chech-label">{$_('Activer les items du panier tout de suite, mais payer plus tard.')}</label>
                        </div>
                    </div>
                </div>
                <div class="row">
                    <label for="tlde-mp-quot" class="col-sm-3 col-form-label pt-0">{$_('Quotation')}</label>
                    <div class="col-sm-9">
                        <div class="form-check">
                            <input class="form-check-input" type="checkbox" id="tlde-mp-quot" bind:checked={form['quotation']} />
                            <label class="form-check-label" for="tlde-mp-quot">{$_('Peut convertir un shopping cart en soumission, nécessitant une action administrative avant de continuer en « paiement différ ».')}</label>
                        </div>
                    </div>
                </div>
                <div class="row">
                    <label for="tlde-mp-cc" class="col-sm-3 col-form-label pt-0">{$_('Carte de crédit')}</label>
                    <div class="col-sm-9">
                        <div class="form-check">
                            <input class="form-check-input" type="checkbox" id="tlde-mp-cc" bind:checked={form['credit card']} />
                            <label class="form-check-label" for="tlde-mp-cc">{$_('Carte de crédit de base (Beanstream / Bambora / Worldline).')}</label>
                        </div>
                    </div>
                </div>
                <div class="row">
                    <label for="tlde-mp-stripe" class="col-sm-3 col-form-label pt-0">Stripe</label>
                    <div class="col-sm-9">
                        <div class="form-check">
                            <input class="form-check-input" type="checkbox" id="tlde-mp-stripe" bind:checked={form['stripe']} />
                            <label class="form-check-label" for="tlde-mp-stripe">{$_('Stripe (carte de crédit).')}</label>
                        </div>
                    </div>
                </div>
                <div class="row">
                    <div class="offset-sm-3 col-sm-9">
                        <p class="help-block" class:text-warning={!form['credit card'] && !form['stripe']}>{$_('L’option de payer immédiatement par carte de crédit est toujours disponible.')}</p>
                        <p class="help-block">{$_('Si « Stripe » ou « Carte de crédit » est coché, ce sera le choix offert à l’utilisateur. Si aucun n’est sélectionné, l’option par défaut sera offerte.')}</p>
                        {#if saving}
                            <button type="button" class="btn btn-info" disabled
                                ><div class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
                                Un moment…</button
                            >
                        {:else}
                            <button type="submit" class="btn btn-outline-secondary" disabled={!changed}>{$_('Save')}</button>
                        {/if}
                    </div>
                </div>
            </form>
        </div>
    {/if}
</div>
